import { useParams, useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import { useTripContext } from "../TripContext";
import "../../../css/View/RegistrationDetails.css";
import useApi from "../../CustomHooks/useApi";
import { registrationAPI } from "../../../api/apiClient";
import ConfirmWithPhrasePopup from "../../Common/ConfirmWithPhrasePopup";
import { useToast } from "../../../Contexts/ToastContext";
import getParticipantStatusStyle, {ParticipantStatus} from "../../../utils/getParticipantStatusStyle";
import "../../../css/Common/ParticipantStatus.css";
import Loading from "../../../Loading";
import { systemEmailsAPI } from "../../../api/apiClient";

const RegistrationDetails = () => {
  const { tripData, updateRegistrationInContext, hasExplicitAccess } = useTripContext();
  const navigate = useNavigate();
  const { resGuid, guid: tripGuid } = useParams();
  useEffect(() => {
      if (!resGuid) return;

      registrationAPI.logView(resGuid).catch(() => {});
    }, [resGuid]);

  const registration = tripData?.registrations.find((r) => r.reservationGuid === resGuid);
  const [popupState, setPopupState] = useState(null);
  const { showToast, clearToast } = useToast();
  const [editReminders, setEditReminders] = useState(false);
  const [confirmPopup, setConfirmPopup] = useState(null);
  const { callApi: cancelRegistration, isLoading: isCancellingRegistration } = useApi();
  const { callApi: moveToActive, isLoading: isMovingToActive } = useApi();
  const { callApi: manuallyConfirm, isLoading: isManuallyConfirming } = useApi();
  const { callApi: saveReminders, isLoading: isSavingReminders } = useApi();
  const [editAdditionalInfo, setEditAdditionalInfo] = useState(false);
  const { callApi: saveAdditionalInfo, isLoading: isSavingAdditionalInfo } = useApi();
  const { callApi: changePrimaryParticipant, isLoading: isChangingPrimaryParticipant } = useApi();
  const [isSendingChangeMail, setIsSendingChangeMail] = useState(false);
  const [isSendingPaymentMail, setIsSendingPaymentMail] = useState(false);
  const [changePrimaryPopupOpen, setChangePrimaryPopupOpen] = useState(false);

  const primaryCandidates = useMemo(() => {
      return (registration?.participants || [])
        .filter(
          (p) =>
            ![ParticipantStatus.Deleted, ParticipantStatus.Cancelled].includes(p.status)
        )
        .sort((a, b) => a.preferredName.localeCompare(b.preferredName, "pl"));
    }, [registration?.participants]);

      const currentPrimaryParticipant = useMemo(() => {
        return primaryCandidates.find((p) => p.isPrimary) || null;
      }, [primaryCandidates]);

      const [selectedPrimaryParticipantGuid, setSelectedPrimaryParticipantGuid] = useState(
        currentPrimaryParticipant?.guid || ""
      );

       useEffect(() => {
      setSelectedPrimaryParticipantGuid(currentPrimaryParticipant?.guid || "");
    }, [currentPrimaryParticipant?.guid]);


const variant = useMemo(() => {
  const id = registration?.variantGuid;
  return tripData?.variants?.find(v => v.guid === id);
}, [tripData?.variants, registration?.variantGuid]);

const variantStarted = !!variant?.variantStarted;

const [formReminders, setFormReminders] = useState({
  reminder1At: registration?.firstReminderPlannedAt || null,
  reminder2At: registration?.secondEmailReminderDate || null,
  coordinatorNotes: registration?.coordinatorNotes || null
});

const [formAdditionalInfo, setFormAdditionalInfo] = useState({
  departureCity: registration?.additionalInfo?.departureCity || "",
  departureCityInfo: registration?.additionalInfo?.departureCityInfo || "",
  hasPet: registration?.additionalInfo?.hasPet ?? false,
  petInfo: registration?.additionalInfo?.petInfo || "",
  foodInfo: registration?.additionalInfo?.foodInfo || "",
  userComment: registration?.userComment || "",
  hasCar: registration?.additionalInfo?.hasCar ?? false,
  canTakePassengers: registration?.additionalInfo?.canTakePassengers ?? false,
  passengerCount: registration?.additionalInfo?.passengerCount ?? 0
});

if (!registration) return <p>Nie znaleziono rejestracji.</p>;
const EPS = 1e-9; 
const incoming = registration?.incomingTransfersAmount ?? registration?.totalTransfersAmount ?? 0;
const outgoing = registration?.outgoingTransfersAmount ?? 0;
const rawCalculatedBalance =
  (incoming - outgoing) - (registration?.amountToPay ?? 0);

const calculatedBalance = Math.abs(rawCalculatedBalance) < EPS ? 0 : rawCalculatedBalance;

  const statusMap = {
    1: { label: "Nieukończona", color: "#7f8c8d" },
    2: { label: "Lista rezerwowa", color: "#e16a00" },
    3: { label: "Potwierdzona", color: "#0d8732" },
    4: { label: "Oczekuje na zaliczkę", color: "#0081d7" },
    5: { label: "Anulowana", color: "#b91100" },
    6: { label: "Usunięta", color: "#363636" },
  };

  const status = statusMap[
    [5, 6].includes(registration.status)
      ? registration.status
      : !registration.createdAt
      ? 1
      : registration.status
  ];

  const canEdit = !!tripData?.canEditTrip && !!hasExplicitAccess;

  const canChangePrimaryParticipant =
  [2, 3, 4].includes(registration.status) &&
  primaryCandidates.length > 1;

  const openPopup = ({ text, onConfirm }) => {
    setPopupState({ text, onConfirm });
  };

const handleSavePrimaryParticipant = async () => {

  if (
    !selectedPrimaryParticipantGuid ||
    selectedPrimaryParticipantGuid === currentPrimaryParticipant?.guid
  ) {
    closeChangePrimaryPopup();
    return;
  }
  await changePrimaryParticipant(
    () =>
      registrationAPI.changePrimaryParticipant(resGuid, {
        newPrimaryParticipantGuid: selectedPrimaryParticipantGuid,
      }),
    async (response) => {
      await updateRegistrationInContext(response);
      showToast("Zmieniono osobę główną rezerwacji.", "success");
      closeChangePrimaryPopup();
    }
  );
};

  const handleCancel = async () => {
    await cancelRegistration(
      () => registrationAPI.cancelRegistration(resGuid),
      async (response) => {
        await updateRegistrationInContext(response.registration);
        showToast(response.message || "Dane zaktualizowane poprawnie!", "success");
      }
    );
  };

  const handleSaveReminders = async () => {
    await saveReminders(() =>
      registrationAPI.updateReminders(resGuid, {
        firstReminderPlannedAt: formReminders.reminder1At,
        secondReminderPlannedAt: formReminders.reminder2At,
        coordinatorNotes: formReminders.coordinatorNotes
      }), 
      async (response) => {
        await updateRegistrationInContext(response);
        setEditReminders(false);
        showToast("Dane zaktualizowane poprawnie!", "success");
      },
    clearToast());
  };

  const handleMoveToActive = async () => {
    await moveToActive(
      () => registrationAPI.moveToActive(resGuid),
      async (response) => {
        await updateRegistrationInContext(response);
        showToast("Rezerwacja przeniesiona na listę główną.", "success");
      }
    );
  };

  const handleManualConfirm = async () => {
    await manuallyConfirm(
      () => registrationAPI.confirm(resGuid),
      async (response) => {
        await updateRegistrationInContext(response);
        showToast("Rezerwacja została ręcznie potwierdzona.", "success");
      }
    );
  };

  const handleSaveAdditionalInfo = async () => {
    await saveAdditionalInfo(
      () => registrationAPI.updateAdditionalInfo(resGuid, formAdditionalInfo),
      async (response) => {
        await updateRegistrationInContext(response);
        setEditAdditionalInfo(false);
        showToast("Zapisano dodatkowe informacje.", "success");
      }
    );
  };

  const primary = registration.participants.find((p) => p.isPrimary);
  const showCancelIcon = ![5, 6].includes(registration.status);

  const actionLabel = [3, 4].includes(registration.status)
    ? "Anuluj rezerwację"
    : "Usuń rezerwację";

  const actionConfirmText = [3, 4].includes(registration.status)
    ? "Czy na pewno chcesz anulować tę rezerwację?"
    : "Czy na pewno chcesz usunąć tę rezerwację?";

  const formatDateTime = (date) =>
    new Date(date).toLocaleString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });

  const formatISOWith4Digits = (isoString) => {
    const d = new Date(isoString);
    const milliseconds = String(d.getMilliseconds()).padStart(3, "0");
    return `${d.toLocaleString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    })}.${milliseconds}`;
  };

  const handleSendMail = async (type) => {
  try {
    if (type === "change") setIsSendingChangeMail(true);
    else setIsSendingPaymentMail(true);

    showToast("Wysyłanie wiadomości...", "processing");

    await systemEmailsAPI.sendBulk({
      mailType: type,
      reservationGuids: [registration.reservationGuid],
    });

    showToast(
      type === "payment"
        ? "Mail o dopłacie został wysłany."
        : "Mail o zmianie został wysłany.",
      "success"
    );
  } catch (err) {
    console.error(err);
    showToast("Nie udało się wysłać wiadomości", "error");
  } finally {
    setIsSendingChangeMail(false);
    setIsSendingPaymentMail(false);
  }
};

const openMailConfirm = (type) => {
  setConfirmPopup({
    type,
    text:
      type === "payment"
        ? "Czy na pewno chcesz wysłać mail o dopłacie dla tej rezerwacji?"
        : "Czy na pewno chcesz wysłać mail o zmianie dla tej rezerwacji?",
  });
};

const handleCopyFinishLink = async () => {
  try {
    const base = process.env.REACT_APP_TRIPS_BASE || window.location.origin;
    const url = `${base}/register/${registration.variantGuid}?pr=${registration.reservationGuid}`;

    await navigator.clipboard.writeText(url);
    showToast("Skopiowano link do ukończenia rezerwacji.", "success");
  } catch (err) {
    console.error(err);
    try {
      const base = process.env.REACT_APP_TRIPS_BASE || window.location.origin;
      const url = `${base}/register/${registration.variantGuid}?pr=${registration.reservationGuid}`;
      window.prompt("Skopiuj link:", url);
    } catch {}

    showToast("Nie udało się skopiować linku.", "error");
  }
};

const openChangePrimaryPopup = () => {
  setSelectedPrimaryParticipantGuid(currentPrimaryParticipant?.guid || "");
  setChangePrimaryPopupOpen(true);
};

const closeChangePrimaryPopup = () => {
  setSelectedPrimaryParticipantGuid(currentPrimaryParticipant?.guid || "");
  setChangePrimaryPopupOpen(false);
};


  return (
    <div className="registration-container">
      {hasExplicitAccess && <div className="trip-details-buttons">
        {showCancelIcon && (
          <div
            className="trip-details-button-container"
            onClick={() => {
               if (variantStarted) return;
              openPopup({
                text: actionConfirmText,
                onConfirm: handleCancel,
              })
            }
            }
            style={{ pointerEvents: isCancellingRegistration || variantStarted ? "none" : "auto",  opacity: variantStarted ? 0.5 : 1, }}
          >
            <div className="trip-details-button">
              <i className={`fa-solid ${isCancellingRegistration ? "fa-spinner fa-spin" : "fa-ban"} trip-details-button-icon`} />
            </div>
            <span className="trip-details-button-label">{actionLabel}</span>
          </div>
        )}

         {registration.status === 2 && registration.createdAt &&  ( 
          <div
            className="trip-details-button-container"
             onClick={() => {
                if (variantStarted) return;
                openPopup({
                  text: "Czy na pewno chcesz przenieść rezerwację z listy rezerwowej na listę główną?",
                  onConfirm: handleMoveToActive,
                });
              }}
              style={{
                pointerEvents: isMovingToActive || variantStarted ? "none" : "auto",
                opacity: variantStarted ? 0.5 : 1,
              }}
          >
            <div className="trip-details-button">
              <i className={`fa-solid ${isMovingToActive ? "fa-spinner fa-spin" : "fa-circle-check"} trip-details-button-icon`} />
            </div>
            <span className="trip-details-button-label">Przyznaj miejsce</span>
          </div>
         )}

          {registration.status === 4 && registration.createdAt && (
            <div
              className="trip-details-button-container"
             onClick={() => {
              if (variantStarted) return;
              openPopup({
                text: "Czy na pewno chcesz ręcznie potwierdzić tę rezerwację?",
                onConfirm: handleManualConfirm,
              });
            }}
            style={{
              pointerEvents: isManuallyConfirming || variantStarted ? "none" : "auto",
              opacity: variantStarted ? 0.5 : 1,
            }}
            >
              <div className="trip-details-button">
                <i className={`fa-solid ${isManuallyConfirming ? "fa-spinner fa-spin" : "fa-circle-check"} trip-details-button-icon`} />
              </div>
              <span className="trip-details-button-label">Potwierdź ręcznie</span>
            </div>
          )}

            {[3, 4].includes(registration.status) && (
              <div
                className="trip-details-button-container"
                onClick={() => openMailConfirm("change")}
                style={{ pointerEvents: isSendingChangeMail ? "none" : "auto" }}
              >
                <div className="trip-details-button">
                  <i
                    className={`fa-solid ${
                      isSendingChangeMail ? "fa-spinner fa-spin" : "fa-envelope-open-text"
                    } trip-details-button-icon`}
                  />
                </div>
                <span className="trip-details-button-label">Mail o zmianie</span>
              </div>
            )}

            {calculatedBalance < 0 && (
              <div
                className="trip-details-button-container"
                onClick={() => openMailConfirm("payment")}
                style={{ pointerEvents: isSendingPaymentMail ? "none" : "auto" }}
              >
                <div className="trip-details-button">
                  <i
                    className={`fa-solid ${
                      isSendingPaymentMail ? "fa-spinner fa-spin" : "fa-sack-dollar"
                    } trip-details-button-icon`}
                  />
                </div>
                <span className="trip-details-button-label">Mail o dopłacie</span>
              </div>
            )}
           {hasExplicitAccess && <div
              className="trip-details-button-container"
              onClick={() =>
                navigate(`/trips/${tripGuid}/variants/${registration.variantGuid}/registrations/${resGuid}/emailHistory`)
              }
            >
              <div className="trip-details-button">
                <i className="fa-solid fa-envelope trip-details-button-icon" />
              </div>
              <span className="trip-details-button-label">Emaile</span>
            </div>
            }
            {registration.createdAt == null && ![5, 6].includes(registration.status) && (
              <div
                className="trip-details-button-container"
                onClick={handleCopyFinishLink}
              >
                <div className="trip-details-button">
                  <i className="fa-solid fa-link trip-details-button-icon" />
                </div>
                <span className="trip-details-button-label">Link do ukończ.</span>
              </div>
            )}

      </div>
      }

      {isSavingReminders ? (
  <div className="registration-card">
    <h2 className="rd-reservation-heading">
      Rezerwacja {registration.reservationCode} <i className="fa-solid fa-spinner fa-spin" />
    </h2>
    <Loading text="Zapisywanie..." />
  </div>
) : (
  <div className="registration-card">
    <h2 className={`rd-reservation-heading ${registration.status === 6 ? "participantstrike-through" : ""}`}>
      Rezerwacja {registration.reservationCode}
      { canEdit && (
        <div
          className="reg-edit-icon"
          onClick={() => editReminders ? handleSaveReminders() : setEditReminders(true)}
        >
          {isSavingReminders ? (
            <i className="fa-solid fa-spinner fa-spin" />
          ) : (
            <i className={`fa-solid ${editReminders ? "fa-floppy-disk" : "fa-pen-to-square"}`} />
          )}
        </div>
      )}
    </h2>

      {registration.createdAt ? (
        <div className="main-person-info">
          <div className="main-person-details">
            <strong>{primary.preferredName}</strong>
            <span>📧 {primary.email}</span>
            <span>📞 {primary.phone}</span>
          </div>

          {canChangePrimaryParticipant && (
            <button
              type="button"
              className="main-person-change-button"
              onClick={() => {
                if (variantStarted || isChangingPrimaryParticipant) return;
                openChangePrimaryPopup();
              }}
              disabled={variantStarted || isChangingPrimaryParticipant}
            >
              <i
                className={`fa-solid ${
                  isChangingPrimaryParticipant ? "fa-spinner fa-spin" : "fa-gear"
                }`}
              /> 
            </button>
          )}
        </div>
      ) : (
        <div className="main-person-info main-person-info--column">
          <span><strong>Utworzona przez:</strong> {registration.createdByEmail}</span>
          <span><strong>Telefon:</strong> {registration.createdByPhone ?? "---"}</span>
        </div>
      )}

        <ul className="detail-rd-list">
          <li><strong>Status</strong>{" "}
            <span style={{ color: status?.color, fontWeight: "bold" }}>
              {status?.label}
            </span>
          </li>
          <li><strong>Czas rezerwacji:</strong> {formatISOWith4Digits(registration.prereservationAt)}</li>
          <li><strong>Koszt bazowy:</strong> {registration.baseCost != null ? `${registration.baseCost.toFixed(2)} zł` : "-"}</li>
          <li><strong>Zaliczka:</strong> {registration.prepaymentCost != null ? `${registration.prepaymentCost.toFixed(2)} zł` : "-"}</li>
          {![3, 5, 6].includes(registration.status) && (
              <>
            <li className="reminder-line">
              <span className="reminder-label">Data ponaglenia 1:</span>
              {editReminders && !registration.firstReminderSent ? (
                <input
                  type="datetime-local"
                  className="inline-input"
                  value={formReminders.reminder1At?.substring(0, 16) || ""}
                  onChange={(e) =>
                    setFormReminders({ ...formReminders, reminder1At: e.target.value })
                  }
                />
              ) : (
                <span className="reminder-value">
                  {registration.firstReminderSent && (
                    <i className="fa-solid fa-check reminder-check-icon" title="Wysłano" />
                  )}{" "}
                  {registration.firstReminderPlannedAt
                    ? formatDateTime(registration.firstReminderPlannedAt)
                    : "—"}
                </span>
              )}
            </li>


            <li className="reminder-line">
              <span className="reminder-label">Data ponaglenia 2:</span>
              {editReminders && !registration.secondReminderSent ? (
                <input
                  type="datetime-local"
                  className="inline-input"
                  value={formReminders.reminder2At?.substring(0, 16) || ""}
                  onChange={(e) =>
                    setFormReminders({ ...formReminders, reminder2At: e.target.value })
                  }
                />
              ) : (
                <span className="reminder-value">
                  {registration.secondReminderSent && (
                    <i className="fa-solid fa-check reminder-check-icon" title="Wysłano" />
                  )}{" "}
                  {registration.secondEmailReminderDate
                    ? formatDateTime(registration.secondEmailReminderDate)
                    : "—"}
                </span>
              )}
            </li>
              </>
            )}
          {registration.confirmedAt && <li><strong>Potwierdzona:</strong> {formatDateTime(registration.confirmedAt)}</li>}
          {registration.cancelledAt && <li><strong>Anulowana:</strong> {formatDateTime(registration.cancelledAt)}</li>}
          {registration.deletedAt && <li><strong>Usunięta:</strong> {formatDateTime(registration.deletedAt)}</li>}
          <li><strong>PAX:</strong> {registration.pax}</li>
          {registration.createdAt && (
            <>
              {registration.contractDeliveryMethod && (
                <li>
                  <strong>Umowa:</strong>{" "}
                  {registration.contractDeliveryMethod === 1 ? "Elektroniczna" : "Papierowa"}
                </li>
              )}
              {registration.contractDeliveryMethod === 2 && registration.postalAddress && (
                <li><strong>Adres:</strong> {registration.postalAddress}</li>
              )}
              <li>
                <strong>Komentarz koord.:</strong>{" "}
                {canEdit && editReminders
                  ? <input
                      type="text"
                      className="inline-input"
                      value={formReminders.coordinatorNotes || ""}
                      onChange={(e) =>
                        setFormReminders({ ...formReminders, coordinatorNotes: e.target.value })
                      }
                    />
                  : (registration.coordinatorNotes || "—")}
              </li>


            </>
          )}
        </ul>
  </div>
)}

      {registration.additionalInfo && (
        <div className="registration-card">
          <h2 className="rd-section-heading">
              Dodatkowe informacje
              {canEdit && (
                <div
                  className="reg-edit-icon"
                  onClick={() => editAdditionalInfo ? handleSaveAdditionalInfo() : setEditAdditionalInfo(true)}
                >
                  {isSavingAdditionalInfo
                    ? <i className="fa-solid fa-spinner fa-spin" />
                    : <i className={`fa-solid ${editAdditionalInfo ? "fa-floppy-disk" : "fa-pen-to-square"}`} />}
                </div>
              )}
            </h2>
          <ul className="detail-rd-list">
          <li>
          <strong>Miasto:</strong>{" "}
          {editAdditionalInfo ? (
            <>
              <input
                type="text"
                className="inline-input"
                placeholder="Miasto"
                value={formAdditionalInfo.departureCity}
                onChange={(e) => setFormAdditionalInfo({ ...formAdditionalInfo, departureCity: e.target.value })}
              />
            </>
          ) : (
            <>
              {formAdditionalInfo.departureCity}
            </>
          )}
        </li>
        <li>
          <strong>Inf. o lokalizacji:</strong>{" "}
          {editAdditionalInfo ? (
            <>
<input type="text" className="inline-input" placeholder="Dodatkowe info" value={formAdditionalInfo.departureCityInfo} onChange={(e) => setFormAdditionalInfo({ ...formAdditionalInfo, departureCityInfo: e.target.value })} />
            </>
          ) : (
            <>
              {formAdditionalInfo.departureCityInfo}
            </>
          )}
        </li>

        <li>
  <strong>Auto:</strong>{" "}
  {editAdditionalInfo ? (
    <input
      type="checkbox"
      checked={formAdditionalInfo.hasCar}
      onChange={(e) => setFormAdditionalInfo({ ...formAdditionalInfo, hasCar: e.target.checked })}
    />
  ) : formAdditionalInfo.hasCar ? "Tak" : "Nie"}
</li>

{formAdditionalInfo.hasCar && (
  <>
    <li>
      <strong>Może zabrać współp.:</strong>{" "}
      {editAdditionalInfo ? (
        <input
          type="checkbox"
          checked={formAdditionalInfo.canTakePassengers}
          onChange={(e) => setFormAdditionalInfo({ ...formAdditionalInfo, canTakePassengers: e.target.checked })}
        />
      ) : formAdditionalInfo.canTakePassengers ? "Tak" : "Nie"}
    </li>

    {formAdditionalInfo.canTakePassengers && (
      <li>
        <strong>Wolne miejsca:</strong>{" "}
        {editAdditionalInfo ? (
          <input
            type="number"
            className="inline-input"
            min={0}
            value={formAdditionalInfo.passengerCount}
            onChange={(e) => setFormAdditionalInfo({ ...formAdditionalInfo, passengerCount: parseInt(e.target.value) })}
          />
        ) : (formAdditionalInfo.passengerCount ?? "—")}
      </li>
    )}
  </>
)}
<li>
  <strong>Zwierzęta:</strong>{" "}
  {editAdditionalInfo ? (
    <input
      type="checkbox"
      checked={formAdditionalInfo.hasPet}
      onChange={(e) =>
        setFormAdditionalInfo({
          ...formAdditionalInfo,
          hasPet: e.target.checked
        })
      }
    />
  ) : formAdditionalInfo.hasPet ? "Tak" : "Nie"}
</li>

{formAdditionalInfo.hasPet && (
  <li>
    <strong>Inf. o zwierzętach:</strong>{" "}
    {editAdditionalInfo ? (
      <input
        type="text"
        className="inline-input"
        placeholder="Np. mały pies, transport w klatce"
        value={formAdditionalInfo.petInfo}
        onChange={(e) =>
          setFormAdditionalInfo({
            ...formAdditionalInfo,
            petInfo: e.target.value,
          })
        }
      />
    ) : formAdditionalInfo.petInfo || "—"}
  </li>
)}


{editAdditionalInfo ? (
  <>
    <li>
      <strong>Inf. o diecie:</strong>{" "}
      <input
        type="text"
        className="inline-input"
        value={formAdditionalInfo.foodInfo}
        onChange={(e) => setFormAdditionalInfo({ ...formAdditionalInfo, foodInfo: e.target.value })}
      />
    </li>
    <li>
      <strong>Komentarz uż.:</strong>{" "}
      <input
        type="text"
        className="inline-input"
        value={formAdditionalInfo.userComment}
        onChange={(e) => setFormAdditionalInfo({ ...formAdditionalInfo, userComment: e.target.value })}
      />
    </li>
  </>
) : (
  <>
    {formAdditionalInfo.foodInfo && <li><strong>Inf. o diecie:</strong> {formAdditionalInfo.foodInfo}</li>}
    <li><strong>Komentarz uż.:</strong> {formAdditionalInfo.userComment || "—"}</li>
  </>
)}

          </ul>
        </div>
      )}

      <div className="registration-card">
        <h2 className="rd-section-heading">
          Saldo{" "}
          <span style={{
            color: calculatedBalance > 0 ? "#198754" :
                   calculatedBalance < 0 ? "#c0392b" : "#7f8c8d",
          }}>
            ({calculatedBalance > 0 ? "+" : calculatedBalance < 0 ? "-" : ""}
            {Math.abs(calculatedBalance).toFixed(2)} zł)
          </span>
        </h2>
        <ul className="detail-rd-list">
          <li className="rd-amount-row">
            <span className="rd-amount-label">Kwota do zapłaty:</span>
            <span className="rd-amount-box rd-total">
              {registration.amountToPay != null ? `${registration.amountToPay.toFixed(2)} zł` : "-"}
            </span>
          </li>
          <li className="rd-amount-row">
            <span className="rd-amount-label">Wpłacono:</span>
            <span className="rd-amount-box rd-paid">
              {registration.totalTransfersAmount != null ? `${registration.totalTransfersAmount.toFixed(2)} zł` : "0 zł"}
            </span>
          </li>
          <li className="rd-amount-row">
            <span className="rd-amount-label">Zwroty:</span>
            <span className="rd-amount-box rd-refunds">
              {outgoing != null ? `${outgoing.toFixed(2)} zł` : "0 zł"}
            </span>
          </li>

        </ul>
      </div>

      {registration.participants?.length > 0 && (
        <div className="registration-card">
          <h2 className="rd-section-heading">Osoby</h2>
          <ul className="participant-reg-list">
            {[...registration.participants]
              .filter(p => p.status !== ParticipantStatus.Deleted)
              .sort((a, b) => a.preferredName.localeCompare(b.preferredName, "pl"))
              .map((p) => {
                const style = getParticipantStatusStyle(p.status);
                return (
                  <li
                    key={p.guid}
                    className={`participant-reg-item ${style.statusClass}`}
                    onClick={() =>
                      navigate(`/trips/${tripGuid}/variants/${registration.variantGuid}/participants/${p.guid}`)
                    }
                  >
                    <i className={`fa-solid ${style.icon} participant-icon ${style.statusClass}`}></i>
                    <span className={`participant-name ${style.statusClass}`}>
                      {p.preferredName}
                      {p.totalCost != null && (
                          <span className="participant-cost-inline">
                            {" "} (Koszty: {p.totalCost.toFixed(2)} zł)
                          </span>
                        )}
                    </span>
                  </li>
                );
              })}
            {registration.participants.some(p => p.status === ParticipantStatus.Deleted) && (
              <>
                <li className="participant-section-divider">
                  <span>
                    Usunięte osoby uczestniczące ({registration.participants.filter(p => p.status === ParticipantStatus.Deleted).length})
                  </span>
                </li>
                {registration.participants
                  .filter(p => p.status === ParticipantStatus.Deleted)
                  .sort((a, b) => a.preferredName.localeCompare(b.preferredName, "pl"))
                  .map((p) => {
                    const style = getParticipantStatusStyle(p.status);
                    return (
                      <li
                        key={p.guid}
                        className={`participant-reg-item ${style.statusClass}`}
                        onClick={() =>
                          navigate(`/trips/${tripGuid}/variants/${registration.variantGuid}/participants/${p.guid}`)
                        }
                      >
                        <i className={`fa-solid ${style.icon} participant-icon ${style.statusClass}`}></i>
                        <span className={`participant-name ${style.strikeThrough ? "participantstrike-through" : ""}`}>
                          {p.preferredName}
                        </span>
                      </li>
                    );
                  })}
              </>
            )}
          </ul>
        </div>
      )}

      {popupState && (
        <ConfirmWithPhrasePopup
          phrase="okoń"
          text={popupState.text}
          onConfirm={async () => {
            setPopupState(null);
            await popupState.onConfirm();
          }}
          onCancel={() => setPopupState(null)}
        />
      )}

      {confirmPopup && (
  <ConfirmWithPhrasePopup
    phrase="okoń"
    text={confirmPopup.text}
    onConfirm={async () => {
       await handleSendMail(confirmPopup.type);
      setConfirmPopup(null);
    }}
    onCancel={() => setConfirmPopup(null)}
  />
)}

{changePrimaryPopupOpen && (
  <ConfirmWithPhrasePopup
    phrase="okoń"
    text={
      <div>
        <p>Wybierz nową osobę główną rezerwacji:</p>

        <select
          className="inline-input"
          value={selectedPrimaryParticipantGuid}
          onChange={(e) => setSelectedPrimaryParticipantGuid(e.target.value)}
        >
          {primaryCandidates.map((p) => (
            <option key={p.guid} value={p.guid}>
              {p.preferredName}
              {p.guid === currentPrimaryParticipant?.guid ? " (aktualna)" : ""}
            </option>
          ))}
        </select>

        <p style={{ marginTop: "12px" }}>
          Po zapisaniu zmiany automatycznie zostaną wysłane maile do obu osób:
          dotychczasowej oraz nowej osoby głównej.
        </p>
      </div>
    }
    onConfirm={handleSavePrimaryParticipant}
    onCancel={closeChangePrimaryPopup}
  />
)}


    </div>
  );
};

export default RegistrationDetails;
